// קנבס העורך: הבאנר עצמו מרונדר דרך BBRuntime.mount (אותו runtime של היצוא),
// ומעליו שכבת בחירה: גרירה, ידיות שינוי גודל וסיבוב. זום משנה תצוגה בלבד.
import { useEffect, useRef } from 'react';
import type { Controller } from '@bb/renderer';
import type { BannerDoc, Layer } from '@bb/schema';
import { ensureFonts } from './BannerView';
import { updateLayer } from './docUtils';
import type { AssetMap } from './types';

type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

interface Props {
  doc: BannerDoc;
  assets: AssetMap;
  elapsed: number;
  zoom: number;
  snap: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (doc: BannerDoc, coalesceKey: string) => void;
}

interface Drag {
  kind: 'move' | 'resize' | 'rotate';
  handle?: Handle;
  id: string;
  key: string;
  mx: number; my: number;
  l: Layer;
  cx: number; cy: number; // מרכז השכבה במסך
}

export default function Canvas({ doc, assets, elapsed, zoom, snap, selectedId, onSelect, onChange }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const ctl = useRef<Controller | null>(null);
  const latest = useRef({ doc, zoom, snap, onChange });
  latest.current = { doc, zoom, snap, onChange };
  const drag = useRef<Drag | null>(null);

  // רינדור דרך ה-runtime
  useEffect(() => {
    if (!root.current || !globalThis.BBRuntime) return;
    ensureFonts(assets);
    const c = globalThis.BBRuntime.mount(root.current, doc, assets, { autoplay: false });
    c.seek(elapsed);
    ctl.current = c;
    return () => { c.destroy(); ctl.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, assets]);
  useEffect(() => { ctl.current?.seek(elapsed); }, [elapsed]);
  useEffect(() => {
    let alive = true;
    document.fonts?.ready.then(() => { if (alive) ctl.current?.seek(elapsed); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const { doc: cur, zoom: z, snap: sn, onChange: change } = latest.current;
      const dx = (e.clientX - d.mx) / z, dy = (e.clientY - d.my) / z;
      if (d.kind === 'move') {
        let x = d.l.x + dx, y = d.l.y + dy;
        if (sn && !e.altKey) ({ x, y } = snapPos(cur, d.l, x, y, 5 / z));
        change(updateLayer(cur, d.id, (l) => { l.x = Math.round(x); l.y = Math.round(y); }), d.key);
      } else if (d.kind === 'resize' && d.handle) {
        const rad = (-(d.l.rotation || 0) * Math.PI) / 180;
        const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
        const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
        let { x, y, width: w, height: h } = d.l;
        const hd = d.handle;
        if (hd.includes('e')) w = d.l.width + lx;
        if (hd.includes('w')) { w = d.l.width - lx; x = d.l.x + lx; }
        if (hd.includes('s')) h = d.l.height + ly;
        if (hd.includes('n')) { h = d.l.height - ly; y = d.l.y + ly; }
        if (e.shiftKey && hd.length === 2) {
          const ratio = d.l.height / d.l.width;
          const nh = w * ratio;
          if (hd.includes('n')) y = d.l.y + (d.l.height - nh);
          h = nh;
        }
        if (w < 4) { if (hd.includes('w')) x -= 4 - w; w = 4; }
        if (h < 4) { if (hd.includes('n')) y -= 4 - h; h = 4; }
        change(updateLayer(cur, d.id, (l) => { l.x = Math.round(x); l.y = Math.round(y); l.width = Math.round(w); l.height = Math.round(h); }), d.key);
      } else if (d.kind === 'rotate') {
        let deg = (Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * 180) / Math.PI + 90;
        if (deg > 180) deg -= 360;
        deg = e.shiftKey ? Math.round(deg / 15) * 15 : Math.round(deg);
        change(updateLayer(cur, d.id, (l) => { l.rotation = deg; }), d.key);
      }
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, []);

  const start = (e: React.PointerEvent, l: Layer, kind: Drag['kind'], handle?: Handle) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(l.id);
    if (l.locked) return;
    const fr = frame.current!.getBoundingClientRect();
    drag.current = {
      kind, handle, id: l.id, key: `${kind}-${l.id}-${Date.now()}`, mx: e.clientX, my: e.clientY, l: structuredClone(l),
      cx: fr.left + (l.x + l.width / 2) * zoom, cy: fr.top + (l.y + l.height / 2) * zoom,
    };
  };

  const onKey = (e: React.KeyboardEvent) => {
    const sel = doc.layers.find((l) => l.id === selectedId);
    if (e.key === 'Escape') { onSelect(null); return; }
    if (!sel || sel.locked) return;
    const step = e.shiftKey ? 10 : 1;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    onChange(updateLayer(doc, sel.id, (l) => { l.x += d[0]; l.y += d[1]; }), `nudge-${sel.id}`);
  };

  const layers = [...doc.layers].filter((l) => l.visible).sort((a, b) => a.zIndex - b.zIndex);
  const sel = doc.layers.find((l) => l.id === selectedId) ?? null;
  const box = (l: Layer): React.CSSProperties => ({ left: l.x * zoom, top: l.y * zoom, width: l.width * zoom, height: l.height * zoom, transform: `rotate(${l.rotation || 0}deg)` });

  return (
    <div className="cv-stage" tabIndex={0} onKeyDown={onKey} aria-label="קנבס הבאנר — חצים להזזת השכבה הנבחרת, Shift לצעד של 10 פיקסלים, Escape לביטול בחירה">
      <div ref={frame} className="cv-frame" style={{ width: doc.width * zoom, height: doc.height * zoom }} dir="ltr">
        <div className="cv-scale" style={{ transform: `scale(${zoom})`, width: doc.width, height: doc.height }}>
          <div ref={root} />
        </div>
        <div className="cv-overlay" onPointerDown={() => onSelect(null)}>
          {layers.map((l) => (
            <div key={l.id} className={`cv-hit ${l.locked ? 'locked' : ''}`} style={box(l)} title={l.name} onPointerDown={(e) => start(e, l, 'move')} />
          ))}
          {sel && (
            <div className={`cv-sel ${sel.locked ? 'locked' : ''}`} style={box(sel)} onPointerDown={(e) => start(e, sel, 'move')}>
              {!sel.locked && HANDLES.map((h) => (
                <span key={h} className={`cv-h cv-h-${h}`} onPointerDown={(e) => start(e, sel, 'resize', h)} aria-hidden="true" />
              ))}
              {!sel.locked && <span className="cv-rot" onPointerDown={(e) => start(e, sel, 'rotate')} title="סיבוב (Shift = קפיצות של 15°)" aria-hidden="true" />}
              <span className="cv-dim mono">{Math.round(sel.x)},{Math.round(sel.y)} · {Math.round(sel.width)}×{Math.round(sel.height)}{sel.rotation ? ` · ${sel.rotation}°` : ''}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** הצמדה לקצוות ולמרכז הבאנר ולקצוות שכבות אחרות */
function snapPos(doc: BannerDoc, l: Layer, x: number, y: number, th: number) {
  const xs = [0, doc.width / 2, doc.width], ys = [0, doc.height / 2, doc.height];
  for (const o of doc.layers) {
    if (o.id === l.id || !o.visible) continue;
    xs.push(o.x, o.x + o.width / 2, o.x + o.width);
    ys.push(o.y, o.y + o.height / 2, o.y + o.height);
  }
  const best = (pos: number, size: number, targets: number[]) => {
    let out = pos, dist = th + 1;
    for (const t of targets) for (const off of [0, size / 2, size]) {
      const dd = Math.abs(pos + off - t);
      if (dd < dist) { dist = dd; out = t - off; }
    }
    return out;
  };
  return { x: best(x, l.width, xs), y: best(y, l.height, ys) };
}
